import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Box, IconButton, TableCell, TableCellProps } from "@mui/material";
import React from "react";

import {
  StyledBaseRow,
  StyledTableRow,
  StyledTableSectionRow,
  StyledTypography,
} from "../styled-components";

import { CoverageCell, CoverageTitleCell, NOT_PROVIDED } from "./cells";

type CoverageDividerRowProps = {
  label: string;
  totalPlans: number;
  isWhite?: boolean;
};

const CoverageDividerRow = ({
  label,
  totalPlans,
  isWhite = false,
}: CoverageDividerRowProps) => (
  <StyledTableRow isWhite={isWhite}>
    <TableCell>
      <StyledTypography fontWeight="bold" align="left">
        {label}
      </StyledTypography>
    </TableCell>
    <TableCell colSpan={totalPlans}></TableCell>
  </StyledTableRow>
);

type CoverageHeaderRowProps = {
  children: React.ReactNode;
  isWhite?: boolean;
};

const CoverageHeaderRow = ({
  children,
  isWhite = false,
}: CoverageHeaderRowProps) => (
  <CoverageRow label="" isWhite={isWhite}>
    {children}
  </CoverageRow>
);

type CoverageRowProps = {
  label: string;
  children: React.ReactNode;
  isWhite?: boolean;
};

const CoverageRow = ({
  label,
  children,
  isWhite = false,
}: CoverageRowProps) => (
  <StyledTableRow isWhite={isWhite}>
    <CoverageTitleCell label={label} />
    {children}
  </StyledTableRow>
);

type ArrowRowProps = {
  label: string;
  children: React.ReactNode;
  level?: number;
  isWhite?: boolean;
};

const CoverageArrowRow = ({
  label,
  children,
  level = 1,
  isWhite = false,
}: ArrowRowProps) => (
  <StyledTableRow isWhite={isWhite}>
    <TableCell>
      <Box display="flex" alignItems="center" columnGap={0.25} ml={3 * level}>
        <SubdirectoryArrowRightIcon fontSize="small" />
        <StyledTypography>{label || NOT_PROVIDED}</StyledTypography>
      </Box>
    </TableCell>
    {children}
  </StyledTableRow>
);

type NotProvidedRowProps = {
  colSpan: number;
  notProvidedText?: string;
};

const NotProvidedRow = ({ colSpan, notProvidedText }: NotProvidedRowProps) => (
  <StyledTableRow isWhite>
    <CoverageCell
      colSpan={(colSpan || 1) + 1}
      value={
        notProvidedText || "Your carrier does not currently send this data"
      }
    />
  </StyledTableRow>
);

const MessageRow = ({
  message,
  colSpan,
}: {
  message: string;
  colSpan: number;
}) => (
  <StyledTableRow isWhite>
    <CoverageCell colSpan={colSpan + 1} value={message} />
  </StyledTableRow>
);

type SectionRowProps = {
  children: React.ReactNode;
  colSpan: number;
  hasSeparator?: boolean;
  textAlign?: TableCellProps["align"];
  fontSize?: string;
  sxCell?: object;
  keyRow?: string;
  onClick?: () => void;
};
const SectionRow = ({
  children,
  colSpan,
  hasSeparator = true,
  textAlign = "center" as TableCellProps["align"],
  fontSize = "1rem",
  sxCell,
  keyRow,
  onClick,
}: SectionRowProps) => (
  <>
    {hasSeparator && (
      <StyledBaseRow key={`${keyRow}-empty`}>
        <TableCell colSpan={colSpan} align="center" />
      </StyledBaseRow>
    )}
    <StyledTableSectionRow key={keyRow}>
      <TableCell
        colSpan={colSpan}
        align={textAlign}
        sx={{ fontWeight: 600, fontSize, ...sxCell }}
        onClick={onClick}
      >
        {children}
      </TableCell>
    </StyledTableSectionRow>
  </>
);

type HeaderSectionTitleProps = {
  colSpan: number;
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  hasSeparator?: boolean;
};

const HeaderSectionTitle = ({
  colSpan,
  title,
  isOpen,
  setIsOpen,
  hasSeparator = true,
}: HeaderSectionTitleProps) => (
  <SectionRow
    textAlign="left"
    colSpan={colSpan}
    sxCell={{ padding: "0.5rem 1rem" }}
    hasSeparator={hasSeparator}
    onClick={() => setIsOpen(!isOpen)}
  >
    <IconButton
      aria-label="expand row"
      size="small"
      onClick={() => setIsOpen(!isOpen)}
    >
      {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
    {title}
  </SectionRow>
);

export {
  CoverageArrowRow,
  CoverageDividerRow,
  CoverageHeaderRow,
  CoverageRow,
  HeaderSectionTitle,
  MessageRow,
  NotProvidedRow,
  SectionRow,
};
